<template>
  <div
    ref="navCont"
    data-tst="repchart_nav"
    class="repchartnav"
  >
    <CustomScrollbar
      class="repchartnav_scroller"
      :style="`--size-handle: ${handleSize}`"
      style="
        --clr-handle-back: rgba(255, 255, 255, 0.325);
        --clr-handle-back-active: rgba(255, 255, 255, 0.425);
      "
      :percent="scrolledPercent"
      :hasHandle="hasHandle"
      :direction="direction"
      @update="handleScrollUpdate"
    >
      <template #back>
        <canvas
          id="sheet_reports"
          :width="canvas.width"
          :height="canvas.height"
          style="border-radius: 4px; overflow: hidden; height: 100%; width: 100%"
          :style="canvasStyle"
        ></canvas>
      </template>
    </CustomScrollbar>

    <div
      v-if="hasLegend"
      class="repchartnav_legend cursor-default"
    >
      <div
        class="truncate text-center"
        style="
          width: 3.5em;
          position: absolute;
          left: 0;
          transform: translate(-100%, 0);
          padding: 0 0.25em 0 0;
        "
        :title="legendTitle"
        v-html="legendTitle"
      ></div>
      <div
        class="repchartnav_legend_items"
        :class="`repchartnav_legend_items_sort_${viewProps.flop_sortBy}`"
      >
        <div
          v-for="(item, idx) in legendItems"
          :key="item.id + idx"
          class="repchartnav_legend_items_item"
        >
          <div
            class="repchartnav_legend_items_item_content"
            :style="`left:${item.left}px`"
          >
            {{ item.id }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'

import format_mix from '@/mixins/format_mix'
import CustomScrollbar from '@/components/lib/CustomScrollbar'
import { getGroupedActions } from '@/helpers/grouping'

export default {
  name: 'RepChartNav',
  components: {
    CustomScrollbar,
  },
  mixins: [format_mix],
  props: {
    items: Array,
    useHex: Boolean,
    grouping: String,
    hasLegend: Boolean,
    hero: Object,
    opponent: Object,

    showBetsIn: String,
    // grouping: String,
    viewProps: Object,
    scrolledPercent: Number,
    colWidth: Number,
    colHeight: Number,
    hasHandle: Boolean,
    canvasStyle: String,
    direction: { default: 'horizontal' },
    navResizeDeps: [Object, String, Number, Array],
    // direction: { default: 'vertical' },
  },
  emits: ['scroll:update'],
  data() {
    return {
      canvas: { height: 0, width: 0 },
      handleSize: '0px',
      legendItems: [],
      legendTitle: '',
      legendReversed: false,
    }
  },
  mounted() {
    this.getNavColumns()
  },
  methods: {
    handleScrollUpdate({ percentY, percentX } = {}) {
      this.$emit('scroll:update', { percentY, percentX })
    },
    getNavColumns() {
      if (!this.viewProps) return
      // console.log('getNavColumns', this.viewProps)
      requestAnimationFrame(() => {
        const cont = this.$refs.navCont
        if (cont) {
          const { offsetWidth, offsetHeight } = cont
          this.canvas = {
            height: offsetHeight,
            width: offsetWidth,
          }
          const colWidth = offsetWidth / this.items.length
          const colHeight = offsetHeight / this.items.length

          const visibleColumns = this.colHeight
            ? offsetHeight / this.colHeight
            : offsetWidth / this.colWidth

          const numWidth = visibleColumns * colWidth
          const numHeight = visibleColumns * colHeight
          this.handleSize = this.colHeight ? `${numHeight}px` : `${numWidth}px`

          nextTick(() => {
            const canvas = document.getElementById('sheet_reports')
            if (!canvas) return
            const context = canvas.getContext('2d')

            context.clearRect(0, 0, canvas.width, canvas.height)
            const l = this.items.length

            if (this.hasLegend) {
              const vp = this.viewProps
              const vpAction = vp.flop_sortByAction
              const vpSortValue = vp.flop_sortByValue

              const unit = vpSortValue.includes('_ev') ? '' : '%'
              const decimal = vpSortValue.includes('_ev') ? 2 : 0

              const firstItem = this.items[0]
              const lastItem = this.items[this.items.length - 1]
              const reversed =
                vp.flop_sortBy === 'idx' ? firstItem?.data_idx > lastItem?.data_idx : false

              let cards = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A']
              cards = reversed ? cards.reverse() : cards
              const ll = l - 1
              const getLeft = reversed
                ? i => colWidth * Math.abs(i.data_idx - ll)
                : i => colWidth * i.data_idx
              let legendItems = []
              if (vp.flop_sortBy === 'idx') {
                legendItems = cards
                  .map(cc => this.items.find(i => i.id[0] === cc))
                  .filter(i => i)
                  .map((i, idx) => ({
                    id: i.id[0],
                    idx: i.data_idx,
                    left: getLeft(i),
                  }))

                // space too clos items by few px
                const movePx = 8
                if (reversed) {
                  legendItems = legendItems.reverse()

                  let stepX = legendItems[0].left
                  legendItems = legendItems.map((item, idx) => {
                    if (stepX - movePx < item.left) {
                      stepX -= movePx
                      return {
                        ...item,
                        left: stepX,
                      }
                    }
                    return item
                  })

                  legendItems = legendItems.reverse()
                } else {
                  let stepX = 0
                  legendItems = legendItems.map((item, idx) => {
                    const prevItem = legendItems[idx - 1]
                    if (prevItem) {
                      if ((stepX || movePx) > item.left) {
                        stepX += movePx
                        return {
                          ...item,
                          left: stepX,
                        }
                      }
                      if (stepX + movePx > item.left) {
                        // last stepped item
                        stepX += movePx
                        return {
                          ...item,
                          left: stepX,
                        }
                      }
                    }
                    return item
                  })
                }
              } else {
                legendItems = [
                  this.items[0],
                  this.items[Math.floor(l / 2)],
                  // this.items[Math.ceil(l / 4)],
                  // this.items[Math.ceil(l / 4) * 2],
                  // this.items[step],
                  // this.items[step * 2],
                  this.items[l - 1],
                ]
                  .filter(i => i)
                  .map(i => {
                    if (vp.flop_sortBy === 'action') {
                      const l_action = getGroupedActions({
                        actions: i.solution,
                        grouping: this.grouping,
                        groupValues: ['frequency'],
                        raisesFirst: false,
                      })
                      const r = l_action.find(aa =>
                        vpAction.group_display_name
                          ? aa.group_display_name === vpAction.group_display_name
                          : aa.code === vpAction.code,
                      )
                      // console.log(r, vpAction, i)
                      return r ? `${this.numFixed(r.frequency * 100, 1)}%` : '0%'
                    }
                    return `${this.numFixed(
                      i[vpSortValue] * (unit === '%' ? 100 : 1),
                      decimal,
                    )}${unit}`
                  })
                  .map((item, idx) => ({
                    id: item,
                    idx,
                  }))
              }

              this.legendItems = legendItems
              this.legendReversed = reversed
              this.legendTitle =
                vp.flop_sortBy === 'idx'
                  ? this.$t(`switchers.flops`)
                  : vp.flop_sortBy === 'action'
                    ? `${vpAction.display_name || this.$t(`browse.${vpAction.group_display_name}`)} ${
                        vpAction.raise_amount || ''
                      }`
                    : vp.flop_sortBy === 'value'
                      ? this.$t(`switchers.${vpSortValue}`)
                      : 'XX'
            }

            if (this.viewProps.colValue.key === 'strategy') {
              if (this.direction === 'horizontal') {
                for (let index = 0; index < l; index++) {
                  const { actions } = this.items[index]
                  const x = index * colWidth
                  let totalHeight = 0
                  const al = actions.length
                  for (let aIdx = 0; aIdx < al; aIdx++) {
                    const action = actions[aIdx]
                    const height = offsetHeight * action.frequency
                    context.fillStyle = !this.useHex
                      ? getComputedStyle(document.body).getPropertyValue(
                          `--clr-${action.css_class}`,
                        )
                      : action.hex
                    context.fillRect(
                      x,
                      this.canvas.height - totalHeight - height,
                      offsetWidth,
                      height,
                    )
                    totalHeight += height
                    if (aIdx === al - 1) {
                      context.fillStyle = '#121212'
                      context.fillRect(x, 0, offsetWidth, this.canvas.height - totalHeight)
                    }
                  }
                }
              } else if (this.direction === 'vertical') {
                for (let index = 0; index < l; index++) {
                  const { actions } = this.items[index]
                  const y = index * colHeight
                  let totalWidth = 0
                  const al = actions.length
                  for (let aIdx = 0; aIdx < al; aIdx++) {
                    const action = actions[aIdx]
                    const width = offsetWidth * action.frequency
                    context.fillStyle = !this.useHex
                      ? getComputedStyle(document.body).getPropertyValue(
                          `--clr-${action.css_class}`,
                        )
                      : action.hex
                    context.fillRect(totalWidth, y, width, this.canvas.height)
                    totalWidth += width
                    if (aIdx === al - 1) {
                      context.fillStyle = '#121212'
                      context.fillRect(
                        totalWidth,
                        y,
                        this.canvas.width - totalWidth,
                        this.canvas.height,
                      )
                    }
                  }
                }
              }
            } else {
              const pLength = this.viewProps.colValue.val === 'hero_opponent' ? 2 : 1

              const pFirst =
                pLength === 2
                  ? 'oop'
                  : this.viewProps.colValue.val === 'hero'
                    ? this.hero.relative_postflop_position.toLowerCase()
                    : this.opponent.relative_postflop_position.toLowerCase()
              const pSecond = 'ip'

              const clrProp = `${this.viewProps.colValue.key}Clr`
              const heightProp = `${this.viewProps.colValue.key}_normalized`
              if (this.direction === 'horizontal') {
                if (pLength === 1) {
                  for (let index = 0; index < l; index++) {
                    const player = this.items[index][pFirst]
                    const hex = getComputedStyle(document.body).getPropertyValue(player[clrProp])
                    const x = index * colWidth
                    const height = this.canvas.height * player[heightProp]
                    const y = this.canvas.height - height
                    context.fillStyle = '#272727'
                    context.fillRect(x, 0, offsetWidth, y)
                    context.fillStyle = hex
                    context.fillRect(x, y, offsetWidth, height)
                  }
                } else {
                  for (let index = 0; index < l; index++) {
                    const canvasHeight = this.canvas.height
                    let player = this.items[index][pFirst]
                    let hex = getComputedStyle(document.body).getPropertyValue(player[clrProp])
                    let x = index * colWidth
                    let height = (canvasHeight / 2) * player[heightProp]
                    let y = canvasHeight / 2 - 1 - height

                    context.fillStyle = '#272727'
                    context.fillRect(x, 0, offsetWidth, y)
                    context.fillStyle = hex
                    context.fillRect(x, y, offsetWidth, height)

                    player = this.items[index][pSecond]
                    hex = getComputedStyle(document.body).getPropertyValue(player[clrProp])
                    x = index * colWidth
                    height = (canvasHeight / 2) * player[heightProp]

                    y = canvasHeight / 2 + 1
                    context.fillStyle = hex
                    context.fillRect(x, y, offsetWidth, height)

                    y += height

                    context.fillStyle = '#272727'
                    context.fillRect(x, y, offsetWidth, y)
                  }
                }
              } else if (this.direction === 'vertical') {
                if (pLength === 1) {
                  for (let index = 0; index < l; index++) {
                    const player = this.items[index][pFirst]
                    const hex = getComputedStyle(document.body).getPropertyValue(player[clrProp])
                    const y = index * colHeight
                    const width = this.canvas.width * player[heightProp]
                    context.fillStyle = '#272727'
                    context.fillRect(0, y, colWidth, colHeight)
                    context.fillStyle = hex
                    context.fillRect(0, y, width, colHeight)
                  }
                } else {
                  //
                }
              }
            }
          })
        }
      })
    },
  },

  watch: {
    navResizeDeps() {
      // console.log('navResizeDeps', this.navResizeDeps)
      this.getNavColumns()
      setTimeout(() => {
        this.getNavColumns()
      }, 333)
    },
    viewProps() {
      // console.log('viewProps', this.viewProps)
      // debugger
      this.getNavColumns()
    },
  },
}
</script>

<style lang="scss" scoped>
.repchartnav {
  --height: 5.5rem;
  --clr-back: transparent;
  --radius: 4px;

  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  position: relative;

  cursor: pointer;
  &_scroller {
    height: var(--height);
    width: 100%;
    background: var(--clr-back);
    overflow: hidden;
    border-radius: var(--radius);
    // flex: 1;
  }
  &_legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    line-height: 1;
    padding-top: 0.125rem;
    font-weight: 600;
    color: var(--clr-grey-2);
    height: 1.2rem;
    &_items {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      width: 100%;
      height: 100%;
      &:not(.repchartnav_legend_items_sort_idx) {
        .repchartnav_legend_items_item:first-child {
          .repchartnav_legend_items_item_content {
            transform: translate(50%, 0);
          }
        }
        .repchartnav_legend_items_item:last-child {
          .repchartnav_legend_items_item_content {
            transform: translate(-50%, 0);
          }
        }
      }
      &_item {
        display: flex;
        align-items: center;
        justify-content: center;
        &_content {
          position: absolute;
        }
      }
    }
  }
}
</style>
